import type React from "react"
import { useState } from "react"
import Navbar from "../components/Navbar"
import Footer from "../components/Footer"
import { Search, Filter } from "lucide-react"
import { Link } from "react-router-dom"

const AttachmentsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState("All")
  const [searchTerm, setSearchTerm] = useState("")

  const attachmentCategories = ["All", "Ground Care", "Road Maintenance", "Winter Equipment", "Specialized"]

  const attachments = [
    // Ground Care
    {
      name: "Cylinder Mower",
      category: "Ground Care",
      description: "Precision cutting for sports fields and fine turf",
      image: "/images/cylinder-mower.jpg",
    },
    {
      name: "Front Mower with Collection System",
      category: "Ground Care",
      description: "Efficient grass cutting with integrated collection",
      image: "/images/front-mower-collection.jpg",
    },
    {
      name: "Barrier Mower",
      category: "Ground Care",
      description: "Safe mowing along barriers and guardrails",
      image: "/images/barrier-mower.jpg",
    },
    {
      name: "Flail Mower",
      category: "Ground Care",
      description: "Heavy-duty cutting for rough terrain",
      image: "/images/flail-mower.jpg",
    },
    {
      name: "Boom Mower",
      category: "Ground Care",
      description: "Extended reach for slopes and ditches",
      image: "/images/boom-mower.jpg",
    },
    {
      name: "Heavy Duty Mulcher",
      category: "Ground Care",
      description: "Mulching attachment for vegetation management",
      image: "/images/mulcher.jpg",
    },
    {
      name: "Rotary Mower",
      category: "Ground Care",
      description: "Versatile cutting for various grass types",
      image: "/images/rotary-mower.jpg",
    },
    {
      name: "Watering Arm",
      category: "Ground Care",
      description: "Precision watering system",
      image: "/images/watering-arm.jpg",
    },

    // Road Maintenance
    {
      name: "Front Sweeper",
      category: "Road Maintenance",
      description: "Efficient street and pathway cleaning",
      image: "/images/front-sweeper.jpg",
    },
    {
      name: "High Volume Sweeper",
      category: "Road Maintenance",
      description: "Heavy-duty sweeping for large areas",
      image: "/images/high-volume-sweeper.jpg",
    },
    {
      name: "Street Cleaner",
      category: "Road Maintenance",
      description: "Comprehensive street cleaning solution",
      image: "/images/street-cleaner.jpg",
    },
    {
      name: "City Cleaner",
      category: "Road Maintenance",
      description: "Urban cleaning and maintenance",
      image: "/images/city-cleaner.jpg",
    },
    {
      name: "Surface Cleaner",
      category: "Road Maintenance",
      description: "High-pressure surface cleaning",
      image: "/images/surface-cleaner.jpg",
    },
    {
      name: "High Pressure Washer",
      category: "Road Maintenance",
      description: "Powerful washing for tough cleaning jobs",
      image: "/images/pressure-washer.jpg",
    },
    {
      name: "Patch Planer",
      category: "Road Maintenance",
      description: "Road surface preparation and repair",
      image: "/images/patch-planer.jpg",
    },
    {
      name: "Gully and Sewer Cleaner",
      category: "Road Maintenance",
      description: "Drainage system maintenance",
      image: "/images/gully-cleaner.jpg",
    },
    {
      name: "Line Remover",
      category: "Road Maintenance",
      description: "Road marking removal",
      image: "/images/line-remover.jpg",
    },

    // Winter Equipment
    {
      name: "Snow Plough",
      category: "Winter Equipment",
      description: "Efficient snow removal for roads and paths",
      image: "/images/snow-plough.jpg",
    },
    {
      name: "V Snow Plough",
      category: "Winter Equipment",
      description: "V-shaped plough for heavy snow conditions",
      image: "/images/v-snow-plough.jpg",
    },
    {
      name: "Box Plough",
      category: "Winter Equipment",
      description: "Containment plough for controlled snow removal",
      image: "/images/box-plough.jpg",
    },
    {
      name: "Combi Box Plough & Brush",
      category: "Winter Equipment",
      description: "Combined ploughing and brushing",
      image: "/images/combi-plough-brush.jpg",
    },
    {
      name: "Combi Snow Plough & Brush",
      category: "Winter Equipment",
      description: "Integrated snow plough and brush system",
      image: "/images/combi-snow-plough.jpg",
    },
    {
      name: "Snow Brush",
      category: "Winter Equipment",
      description: "Gentle snow removal for sensitive surfaces",
      image: "/images/snow-brush.jpg",
    },
    {
      name: "Snow Blower",
      category: "Winter Equipment",
      description: "High-capacity snow throwing",
      image: "/images/snow-blower.jpg",
    },
    {
      name: "Ice Breaker",
      category: "Winter Equipment",
      description: "Ice removal and breaking",
      image: "/images/ice-breaker.jpg",
    },
    {
      name: "Salt Spreader",
      category: "Winter Equipment",
      description: "Precise salt and grit distribution",
      image: "/images/salt-spreader.jpg",
    },
    {
      name: "Drop Sander",
      category: "Winter Equipment",
      description: "Sand application for traction",
      image: "/images/drop-sander.jpg",
    },
    {
      name: "De-Icing Sprayer",
      category: "Winter Equipment",
      description: "Liquid de-icing application",
      image: "/images/deicing-sprayer.jpg",
    },

    // Specialized
    {
      name: "CityVac Garbage Vacuum",
      category: "Specialized",
      description: "Efficient debris collection system",
      image: "/images/city-cleaner.jpg",
    },
    {
      name: "Thermal Weed Control",
      category: "Specialized",
      description: "Chemical-free weed elimination",
      image: "/images/thermal-weed.jpg",
    },
    {
      name: "Weed Brush",
      category: "Specialized",
      description: "Mechanical weed removal",
      image: "/images/weed-brush.jpg",
    },
    {
      name: "Leaf Blower",
      category: "Specialized",
      description: "Powerful leaf and debris blowing",
      image: "/images/leaf-blower.jpg",
    },
    {
      name: "Runway Light Cleaner",
      category: "Specialized",
      description: "Airport runway light maintenance",
      image: "/images/runway-light-cleaner.jpg",
    },
    {
      name: "Glycol Recovery Unit",
      category: "Specialized",
      description: "Airport glycol collection and recovery",
      image: "/images/glycol-recovery.jpg",
    },
    {
      name: "High Tip Bucket",
      category: "Specialized",
      description: "High-capacity material handling",
      image: "/images/high-tip-bucket.jpg",
    },
    {
      name: "Stump Grinder",
      category: "Specialized",
      description: "Tree stump removal",
      image: "/images/stump-grinder.jpg",
    },
    {
      name: "Manhole Cutter",
      category: "Specialized",
      description: "Precision manhole cutting",
      image: "/images/manhole-cutter.jpg",
    },
    {
      name: "Solar Panel Cleaner",
      category: "Specialized",
      description: "Solar panel maintenance",
      image: "/images/solar-cleaner.jpg",
    },
    {
      name: "Highway Safety Sign",
      category: "Specialized",
      description: "Mobile safety signage",
      image: "/images/safety-sign.jpg",
    },
    {
      name: "Rear Body",
      category: "Specialized",
      description: "Cargo and equipment transport",
      image: "/images/rear-body.jpg",
    },
    {
      name: "Rear Tipping Body",
      category: "Specialized",
      description: "Hydraulic tipping body for materials",
      image: "/images/rear-tipping-body.jpg",
    },
  ]

  const filteredAttachments = attachments.filter((attachment) => {
    const matchesCategory = selectedCategory === "All" || attachment.category === selectedCategory
    const matchesSearch =
      attachment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attachment.description.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesCategory && matchesSearch
  })

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              40+ Specialized Attachments
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-red-500">One Machine,</span>
              <br />
              Endless Applications
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Transform your Multihog tractor with our comprehensive range of specialized attachments. From ground care
              to winter maintenance, we have the perfect solution for every application.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search attachments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              {/* Category Filter */}
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-gray-600" />
                <div className="flex flex-wrap gap-2">
                  {attachmentCategories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        selectedCategory === category
                          ? "bg-red-600 text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Attachments Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <p className="text-gray-600">
                Showing {filteredAttachments.length} attachment{filteredAttachments.length !== 1 ? "s" : ""}
                {selectedCategory !== "All" && ` in ${selectedCategory}`}
                {searchTerm && ` matching "${searchTerm}"`}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredAttachments.map((attachment, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow flex flex-col"
                >
                  <div className="relative">
                    <img
                      src={attachment.image || "https://via.placeholder.com/300x200?text=Attachment"}
                      alt={attachment.name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-red-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                        {attachment.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 flex flex-col flex-grow">
                    <div> {/* Wrapper for text content */}
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{attachment.name}</h3>
                      <p className="text-gray-600 text-sm mb-4">{attachment.description}</p>
                    </div>
                    <div className="flex gap-2 mt-auto"> {/* Changed pt-auto to mt-auto */}
                      <Link to="/contact" className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                        Get Quote
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredAttachments.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No attachments found matching your criteria.</p>
                <button
                  onClick={() => {
                    setSelectedCategory("All")
                    setSearchTerm("")
                  }}
                  className="mt-4 text-red-600 hover:text-red-700 font-medium"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Need Help Choosing the Right Attachment?</h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Our attachment specialists can help you find the perfect solution for your specific applications and
              requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors">
                Speak with a Specialist
              </button>
              <button className="border border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors">
                Download Catalog
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default AttachmentsPage
